/**
 * Automobili Ardent — storefront behaviour.
 *
 * Four concerns, all progressive enhancements over working markup:
 *   1. the header's condensed state once the page scrolls past the hero fold,
 *   2. the desktop mega menu,
 *   3. the mobile full-screen menu overlay, and
 *   4. the scroll reveals (clip-up lines, rise-in cards).
 *
 * Navigation is split by input, not by guesswork: pointer devices 990px and
 * wider get the mega menu, everything else gets the overlay. DESKTOP_NAV is the
 * single source of that split, and the stylesheet hides whichever one is not in
 * play so the two can never both be reachable.
 *
 * Everything re-binds on Shopify's section lifecycle events so the theme editor
 * reflects edits without a reload.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const DESKTOP_NAV = window.matchMedia('(hover: hover) and (min-width: 990px)');

/* ------------------------------------------------------------------ Header */

// Reads a computed background colour as 'dark' or 'light', or null when it is
// too transparent to decide.
function toneOfColor(value) {
  const numbers = value.match(/-?\d*\.?\d+/g)?.map(Number);
  if (!numbers || numbers.length < 3) return null;

  let lightness;
  const alpha = numbers[3] ?? 1;

  if (value.startsWith('rgb')) {
    const [r, g, b] = numbers;
    lightness = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  } else if (value.startsWith('color(srgb')) {
    const [r, g, b] = numbers;
    lightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  } else if (value.startsWith('oklch') || value.startsWith('oklab')) {
    lightness = numbers[0] > 1 ? numbers[0] / 100 : numbers[0];
  } else if (value.startsWith('lab') || value.startsWith('lch')) {
    lightness = numbers[0] / 100;
  } else {
    return null;
  }

  if (alpha < 0.5) return null;
  return lightness < 0.5 ? 'dark' : 'light';
}

// What sits under the header's logo: the first element down the stack that
// declares data-aa-tone, or has a background opaque enough to judge.
function readTone(header, logo) {
  const bar = header.getBoundingClientRect();
  const mark = logo?.getBoundingClientRect();
  const x = mark ? mark.left + mark.width / 2 : window.innerWidth / 2;
  const y = bar.top + bar.height / 2;

  for (const element of document.elementsFromPoint(x, y)) {
    if (header.contains(element)) continue;

    const declared = element.closest('[data-aa-tone]')?.getAttribute('data-aa-tone');
    if (declared === 'dark' || declared === 'light') return declared;

    const tone = toneOfColor(getComputedStyle(element).backgroundColor);
    if (tone) return tone;
  }

  return 'light';
}

function initHeader() {
  const header = document.querySelector('#aa-header');
  if (!header || header.dataset.aaBound === 'true') return;
  header.dataset.aaBound = 'true';

  // From 990px up Horizon locks <html> and scrolls .page-wrapper instead, so
  // window.scrollY never moves on desktop. Read whichever one is scrolling.
  const scroller = document.querySelector('.page-wrapper');

  const onScroll = () => {
    const offset = Math.max(window.scrollY, scroller?.scrollTop ?? 0);
    header.classList.toggle('aa-scrolled', offset > 40);
  };

  // Tone: sample the page under the logo and switch the marks to their dark
  // versions over light backgrounds. Throttled to one read per frame.
  const logo = header.querySelector('.aa-logo');
  const mega = document.querySelector('#aa-mega');
  let toneFrame = 0;

  const updateTone = () => {
    cancelAnimationFrame(toneFrame);
    toneFrame = requestAnimationFrame(() => {
      header.classList.toggle('aa-header--light', readTone(header, logo) === 'light');
    });
  };

  onScroll();
  updateTone();
  window.addEventListener('scroll', onScroll, { passive: true });
  scroller?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', updateTone, { passive: true });
  scroller?.addEventListener('scroll', updateTone, { passive: true });
  window.addEventListener('resize', updateTone, { passive: true });
  window.addEventListener('load', updateTone);

  // The open mega panel is white. Re-read as it opens, and again once its fade
  // out ends, since it still sits under the bar while fading.
  if (mega) {
    new MutationObserver(updateTone).observe(mega, { attributes: true, attributeFilter: ['class'] });
    mega.addEventListener('transitionend', updateTone);
  }

  // The mega panel pads its content down by the header's height, which changes
  // as the header condenses on scroll.
  const publishHeight = () => {
    document.documentElement.style.setProperty('--aa-header-height', `${header.offsetHeight}px`);
  };

  publishHeight();
  if ('ResizeObserver' in window) new ResizeObserver(publishHeight).observe(header);
}

/* --------------------------------------------------------------- Mega menu */

function initMega() {
  const root = document.querySelector('[data-aa-mega-root]');
  const panel = document.querySelector('#aa-mega');
  const button = root?.querySelector('.aa-menu-btn');

  if (!root || !panel || !button || root.dataset.aaBound === 'true') return;
  root.dataset.aaBound = 'true';

  // Hover opens the panel only from the MENU label itself, and only once the
  // pointer rests there briefly, so sweeping across the header on the way to
  // the logo or cart never opens it. The button and panel together still
  // count as "inside" for keyboard focus.
  const trigger = button.querySelector('.aa-menu-text') ?? button;
  const zones = [button, panel];
  const inZones = (node) => zones.some((zone) => zone.contains(node));
  const OPEN_INTENT_DELAY = 120;
  let openTimer = null;

  let closeTimer = null;

  const isOpen = () => panel.classList.contains('aa-mega-open');

  // Previews. Hovering or focusing a category or brand brings its image up in
  // the media panel. The last preview stays up while the pointer travels over
  // to it, so the image itself remains a working link.
  const shots = [...panel.querySelectorAll('[data-aa-mega-shot]')];
  const previews = [...panel.querySelectorAll('[data-aa-mega-preview]')];
  const restingShot = shots[0]?.dataset.aaMegaShot;

  const showShot = (id) => {
    // A category without an image leaves the current one in place.
    if (!shots.some((shot) => shot.dataset.aaMegaShot === id)) return;
    shots.forEach((shot) => shot.classList.toggle('is-active', shot.dataset.aaMegaShot === id));
    previews.forEach((item) => item.classList.toggle('is-current', item.dataset.aaMegaPreview === id));
  };

  previews.forEach((item) => {
    const id = item.dataset.aaMegaPreview;
    item.addEventListener('pointerenter', () => showShot(id));
    item.addEventListener('focus', () => showShot(id));
  });

  const open = () => {
    clearTimeout(closeTimer);
    // The stylesheet hides the panel below this breakpoint; without the same
    // guard a stray event could open something with no way to dismiss it.
    if (!DESKTOP_NAV.matches) return;
    // Each opening starts from the resting image, not the last preview.
    if (!isOpen() && restingShot) showShot(restingShot);
    panel.classList.add('aa-mega-open');
    button.setAttribute('aria-expanded', 'true');
  };

  // A grace period keeps the panel from shutting as the pointer crosses the
  // strip of header between the button and the top of the panel's content.
  const close = (delay = 220) => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    closeTimer = setTimeout(() => {
      if (!isOpen()) return;
      panel.classList.remove('aa-mega-open');
      button.setAttribute('aria-expanded', 'false');
    }, delay);
  };

  trigger.addEventListener('pointerenter', () => {
    clearTimeout(openTimer);
    if (isOpen()) {
      clearTimeout(closeTimer);
      return;
    }
    openTimer = setTimeout(open, OPEN_INTENT_DELAY);
  });

  trigger.addEventListener('pointerleave', () => {
    clearTimeout(openTimer);
    if (isOpen()) close();
  });

  // Once open, the panel holds itself open. A panel that is still fading out
  // does not reopen: its class is already gone, so isOpen() is false.
  panel.addEventListener('pointerenter', () => {
    if (isOpen()) clearTimeout(closeTimer);
  });
  panel.addEventListener('pointerleave', () => close());

  // Focus leaving both the button and the panel closes the panel.
  zones.forEach((zone) => {
    zone.addEventListener('focusout', (event) => {
      if (!inZones(event.relatedTarget)) close(0);
    });
  });

  // Focusing the button alone does not open the panel: it now follows the logo
  // and cart in tab order, so tabbing on would close it before its links were
  // reachable. Enter on the button opens it and moves focus inside instead.
  panel.addEventListener('focusin', () => clearTimeout(closeTimer));

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !isOpen()) return;
    close(0);
    button.focus();
  });

  button.addEventListener('click', (event) => {
    if (!DESKTOP_NAV.matches) return;
    event.preventDefault();

    if (isOpen()) {
      close(0);
      return;
    }

    open();
    requestAnimationFrame(() => panel.querySelector('a, button')?.focus());
  });

  // The button drives whichever menu is in play at the current width.
  const syncControls = () => {
    button.setAttribute('aria-controls', DESKTOP_NAV.matches ? 'aa-mega' : 'aa-menu-overlay');
  };

  syncControls();
  DESKTOP_NAV.addEventListener('change', () => {
    syncControls();
    if (!DESKTOP_NAV.matches) close(0);
  });
}

/* -------------------------------------------------- Overlay (mobile only) */

function initMenu() {
  const overlay = document.querySelector('#aa-menu-overlay');
  const openButton = document.querySelector('.aa-menu-btn');
  const closeButton = overlay?.querySelector('.aa-menu-close');

  if (!overlay || !openButton || overlay.dataset.aaBound === 'true') return;
  overlay.dataset.aaBound = 'true';

  let lastFocused = null;

  const open = () => {
    // Desktop navigates via the mega menu; the overlay is hidden there, and
    // opening it anyway would lock body scroll behind an invisible panel.
    if (DESKTOP_NAV.matches) return;

    lastFocused = document.activeElement;
    overlay.hidden = false;

    // Let the browser paint the un-hidden element before transitioning opacity.
    requestAnimationFrame(() => {
      overlay.classList.add('aa-menu-active');
    });

    openButton.setAttribute('aria-expanded', 'true');
    // iOS Safari ignores overflow on <body> alone; lock the root as well.
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    closeButton?.focus();
  };

  const close = () => {
    overlay.classList.remove('aa-menu-active');
    openButton.setAttribute('aria-expanded', 'false');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';

    const finish = () => {
      overlay.hidden = true;
      overlay.removeEventListener('transitionend', finish);
    };

    if (REDUCED_MOTION.matches) {
      finish();
    } else {
      overlay.addEventListener('transitionend', finish);
    }

    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  };

  openButton.addEventListener('click', open);
  closeButton?.addEventListener('click', close);

  // Following a link, or opening search, should not leave the overlay open
  // behind whatever comes next.
  overlay.querySelectorAll('a[href], [data-aa-menu-dismiss]').forEach((control) => {
    control.addEventListener('click', close);
  });

  // Escape closes; Tab stays inside the open menu, which is a modal dialog.
  document.addEventListener('keydown', (event) => {
    if (!overlay.classList.contains('aa-menu-active')) return;

    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    // Summaries are the dropdown toggles; links inside a closed one have no
    // layout box, so the offsetParent filter already skips them.
    const focusable = [...overlay.querySelectorAll('a[href], button:not([disabled]), summary')].filter(
      (element) => element.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Widening past the breakpoint hides the overlay in CSS. Tear it down properly
  // so body scroll is not left locked behind a panel that is no longer visible.
  DESKTOP_NAV.addEventListener('change', () => {
    if (DESKTOP_NAV.matches && overlay.classList.contains('aa-menu-active')) {
      overlay.classList.remove('aa-menu-active');
      overlay.hidden = true;
      openButton.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  });
}

/* ----------------------------------------------------------------- Reveals */

let revealObserver = null;

function getRevealObserver() {
  if (revealObserver) return revealObserver;

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('aa-in');
        // Reveals play once — stop watching as soon as each has fired.
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
  );

  return revealObserver;
}

function initReveals(root = document) {
  const targets = root.querySelectorAll('[data-aa-reveal]:not([data-aa-observed])');

  targets.forEach((target) => {
    target.dataset.aaObserved = 'true';

    if (REDUCED_MOTION.matches || !('IntersectionObserver' in window)) {
      target.classList.add('aa-in');
      return;
    }

    getRevealObserver().observe(target);
  });
}

/* ------------------------------------------------------------ Fitted type */

// Scales a [data-aa-fit-target] so its set width fills its [data-aa-fit] box.
// Text width grows linearly with font size, so a single measurement is enough.
function initFitText(root = document) {
  root.querySelectorAll('[data-aa-fit]:not([data-aa-fit-bound])').forEach((box) => {
    const target = box.querySelector('[data-aa-fit-target]');
    if (!target) return;
    box.dataset.aaFitBound = 'true';

    let lastWidth = 0;

    const fit = (force = false) => {
      const available = box.clientWidth;
      // Only width matters. Resizing the text changes the box's height, which
      // would otherwise re-trigger the observer in a loop.
      if (!available || (!force && available === lastWidth)) return;
      lastWidth = available;

      const natural = target.getBoundingClientRect().width;
      if (!natural) return;

      const current = parseFloat(getComputedStyle(target).fontSize);
      // A hair under 1 so sub-pixel rounding can never tip the line into clipping.
      target.style.fontSize = `${(current * available * 0.995) / natural}px`;
    };

    fit(true);
    // The first measure may run on the fallback face; refit once Quantico loads.
    document.fonts?.ready.then(() => fit(true));
    if ('ResizeObserver' in window) new ResizeObserver(() => fit()).observe(box);
  });
}

/* -------------------------------------------------------------- Size chart */

// Delegated from the document: the variant picker re-renders its markup on
// every variant change, which would drop listeners bound to the link itself.
function initSizeChart() {
  const root = document.documentElement;
  if (root.dataset.aaSizeChartBound === 'true') return;
  root.dataset.aaSizeChartBound = 'true';

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-aa-size-chart]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.aaSizeChart);
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
      return;
    }

    if (target.closest('[data-aa-size-chart-close]')) {
      target.closest('dialog')?.close();
      return;
    }

    // The content fills an inner wrapper, so a click landing on the dialog
    // element itself can only be on the backdrop.
    if (target instanceof HTMLDialogElement && target.classList.contains('aa-size-chart')) {
      target.close();
    }
  });
}

/* -------------------------------------------------------------------- Boot */

/* ------------------------------------------------------ Split media videos */

// Muted, looped footage in the split media sections. Nothing downloads until a
// video nears the screen, it pauses again once scrolled away, and visitors who
// ask for reduced motion keep the still poster frame instead.
let splitVideoObserver = null;

function playSplitVideo(video) {
  if (REDUCED_MOTION.matches) return;
  video.preload = 'auto';
  video.play().catch(() => {});
}

function initSplitVideos(root = document) {
  const videos = [...root.querySelectorAll('[data-aa-split-video]')].filter(
    (video) => video.dataset.aaSplitBound !== 'true'
  );
  if (!videos.length) return;

  if (!('IntersectionObserver' in window)) {
    videos.forEach((video) => {
      video.dataset.aaSplitBound = 'true';
      playSplitVideo(video);
    });
    return;
  }

  splitVideoObserver ??= new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          playSplitVideo(target);
        } else {
          target.pause();
        }
      });
    },
    { rootMargin: '200px 0px' }
  );

  videos.forEach((video) => {
    video.dataset.aaSplitBound = 'true';
    splitVideoObserver.observe(video);
  });
}

function init(root) {
  initHeader();
  initMega();
  initMenu();
  initSizeChart();
  initReveals(root);
  initFitText(root);
  initSplitVideos(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(document));
} else {
  init(document);
}

// Theme editor: sections are swapped in and out without a page load.
document.addEventListener('shopify:section:load', (event) => {
  init(event.target);
});

document.addEventListener('shopify:section:unload', () => {
  revealObserver?.disconnect();
  revealObserver = null;
  document.querySelectorAll('[data-aa-observed]').forEach((el) => {
    delete el.dataset.aaObserved;
  });
  initReveals(document);
});
